import React, { useState, useRef, useCallback, useEffect } from 'react';
import { CheckCircleFilled } from '@ant-design/icons';

interface SliderCaptchaProps {
  onSuccess: () => void;
  onReset?: () => void;
}

const SLIDER_WIDTH = 44;
const TRACK_HEIGHT = 40;
const THRESHOLD = 0.9; // 滑到 90% 即算通过

const SliderCaptcha: React.FC<SliderCaptchaProps> = ({ onSuccess, onReset }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [offsetX, setOffsetX] = useState(0);
  const [verified, setVerified] = useState(false);
  const trackRef = useRef<HTMLDivElement>(null);
  const startXRef = useRef(0);

  const getMaxOffset = useCallback(() => {
    if (!trackRef.current) return 200;
    return trackRef.current.offsetWidth - SLIDER_WIDTH;
  }, []);

  const handleStart = useCallback((clientX: number) => {
    if (verified) return;
    setIsDragging(true);
    startXRef.current = clientX - offsetX;
  }, [verified, offsetX]);

  const handleMove = useCallback((clientX: number) => {
    if (!isDragging || verified) return;
    const max = getMaxOffset();
    let newX = clientX - startXRef.current;
    newX = Math.max(0, Math.min(newX, max));
    setOffsetX(newX);
  }, [isDragging, verified, getMaxOffset]);

  const handleEnd = useCallback(() => {
    if (!isDragging || verified) return;
    setIsDragging(false);
    const max = getMaxOffset();
    if (offsetX >= max * THRESHOLD) {
      setOffsetX(max);
      setVerified(true);
      onSuccess();
    } else {
      setOffsetX(0);
    }
  }, [isDragging, verified, offsetX, getMaxOffset, onSuccess]);

  // Mouse events
  useEffect(() => {
    if (!isDragging) return;
    const onMouseMove = (e: MouseEvent) => handleMove(e.clientX);
    const onMouseUp = () => handleEnd();
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [isDragging, handleMove, handleEnd]);

  // Touch events
  useEffect(() => {
    if (!isDragging) return;
    const onTouchMove = (e: TouchEvent) => handleMove(e.touches[0].clientX);
    const onTouchEnd = () => handleEnd();
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd);
    return () => {
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
    };
  }, [isDragging, handleMove, handleEnd]);

  // Reset support
  const reset = useCallback(() => {
    setVerified(false);
    setOffsetX(0);
    onReset?.();
  }, [onReset]);

  const progressPercent = trackRef.current
    ? (offsetX / getMaxOffset()) * 100
    : 0;

  return (
    <div style={{ marginBottom: 4 }}>
      <div
        ref={trackRef}
        style={{
          position: 'relative',
          width: '100%',
          height: TRACK_HEIGHT,
          borderRadius: 20,
          background: verified
            ? 'linear-gradient(90deg, #52c41a, #73d13d)'
            : '#f0f2f5',
          border: verified ? '1px solid #52c41a' : '1px solid #d9d9d9',
          overflow: 'hidden',
          userSelect: 'none',
          cursor: verified ? 'default' : 'pointer',
          transition: verified ? 'background 0.3s' : 'none',
        }}
      >
        {/* Progress fill */}
        {!verified && (
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              height: '100%',
              width: `${offsetX + SLIDER_WIDTH / 2}px`,
              background: 'linear-gradient(90deg, #e6f7ff, #91d5ff)',
              borderRadius: '20px 0 0 20px',
              transition: isDragging ? 'none' : 'width 0.3s',
            }}
          />
        )}

        {/* Center text */}
        <div
          style={{
            position: 'absolute',
            width: '100%',
            textAlign: 'center',
            lineHeight: `${TRACK_HEIGHT}px`,
            fontSize: 13,
            color: verified ? '#fff' : '#bfbfbf',
            pointerEvents: 'none',
            fontWeight: verified ? 600 : 400,
            transition: 'color 0.3s',
          }}
        >
          {verified ? (
            <><CheckCircleFilled style={{ marginRight: 6 }} />验证通过</>
          ) : (
            '向右滑动完成验证'
          )}
        </div>

        {/* Slider thumb */}
        {!verified && (
          <div
            onMouseDown={(e) => handleStart(e.clientX)}
            onTouchStart={(e) => handleStart(e.touches[0].clientX)}
            style={{
              position: 'absolute',
              left: offsetX,
              top: 0,
              width: SLIDER_WIDTH,
              height: TRACK_HEIGHT,
              borderRadius: 20,
              background: isDragging
                ? 'linear-gradient(135deg, #1890ff, #096dd9)'
                : 'linear-gradient(135deg, #40a9ff, #1890ff)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontSize: 18,
              fontWeight: 'bold',
              boxShadow: isDragging
                ? '0 2px 8px rgba(24,144,255,0.5)'
                : '0 2px 4px rgba(0,0,0,0.15)',
              cursor: 'grab',
              transition: isDragging ? 'none' : 'left 0.3s, box-shadow 0.2s',
              zIndex: 2,
            }}
          >
            »
          </div>
        )}
      </div>
    </div>
  );
};

export default SliderCaptcha;
